import { useCallback, useMemo } from 'react'
import useSWR from 'swr'
import { useSession } from 'next-auth/react'
import { useRegistry } from '@/hooks/useRegistry'
import { useRepositoryEvents } from '@/hooks/useRepositoryEvents'
import { fetchStatusHistory } from '../lib/fetchStatusHistory'
import { buildStatusHistory, type StatusHistoryEntry } from '../lib/buildStatusHistory'

/**
 * Every workflow status set on a document, newest first, refreshed on repository events.
 */
export const useStatusHistory = ({ uuid, documentType, enabled = true }: {
  uuid: string
  documentType?: string
  enabled?: boolean
}): {
  entries: StatusHistoryEntry[] | undefined
  error: Error | undefined
  isLoading: boolean
} => {
  const { repository } = useRegistry()
  const { data: session } = useSession()
  const accessToken = session?.accessToken

  const key = enabled && repository && accessToken ? ['status-history', uuid] : null

  const { data, error, isLoading, mutate } = useSWR<StatusHistoryEntry[], Error>(key, async () => {
    if (!repository || !accessToken) {
      return []
    }

    const [firstVersion, statusesByName] = await Promise.all([
      // Versions before 2 is the first version, used for the "created" entry
      repository.getHistory({ uuid, accessToken, before: 2n }),
      fetchStatusHistory({ repository, uuid, accessToken })
    ])

    return buildStatusHistory(statusesByName, firstVersion?.versions?.[0])
  })

  const eventTypes = useMemo(
    () => enabled && documentType ? [documentType] : [],
    [enabled, documentType]
  )

  // Only status changes alter the history, so plain saves don't refetch it
  const handleRepositoryEvent = useCallback(
    (event: { uuid?: string, event?: string }) => {
      if (event.event === 'status' && event.uuid === uuid) {
        void mutate()
      }
    },
    [uuid, mutate]
  )

  useRepositoryEvents(eventTypes, handleRepositoryEvent)

  return { entries: data, error, isLoading }
}
